import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  className = '',
  fullWidth = false,
  responsive = false,
  ...props 
}) => {
  const { isMobile, isTablet, getResponsiveValue, getComponentSize } = useTheme();

  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 whitespace-nowrap';
  
  const variants = {
    primary: 'btn-primary',
    secondary: 'btn-secondary', 
    navigation: 'btn-navigation',
    outline: 'border border-border-primary bg-transparent hover:bg-surface-hover text-text-primary focus:ring-border-focus',
    ghost: 'bg-transparent hover:bg-surface-hover text-text-primary focus:ring-border-focus',
    danger: 'bg-error hover:bg-error-hover text-white focus:ring-error shadow-theme-sm',
    success: 'bg-success hover:bg-success-hover text-white focus:ring-success shadow-theme-sm'
  };
  
  // Get responsive size configuration
  const responsiveSize = responsive ? getComponentSize('button', size) : null;
  
  // Default sizes (fallback if not using responsive)
  const defaultSizes = {
    sm: getResponsiveValue({
      mobile: 'px-3 py-2 text-sm',
      desktop: 'px-3 py-2 text-sm'
    }),
    md: getResponsiveValue({
      mobile: 'px-4 py-2.5 text-sm',
      desktop: 'px-4 py-2.5 text-sm'
    }),
    lg: getResponsiveValue({
      mobile: 'px-5 py-3 text-base',
      desktop: 'px-6 py-3 text-base'
    }),
    xl: getResponsiveValue({
      mobile: 'px-6 py-3.5 text-lg',
      desktop: 'px-8 py-4 text-lg'
    })
  };
  
  const disabledClasses = 'opacity-50 cursor-not-allowed';
  
  // Icon size based on screen size
  const iconSize = getResponsiveValue({
    mobile: 'h-4 w-4',
    desktop: 'h-4 w-4'
  });

  // Determine the size classes
  let sizeClasses;
  if (responsive && responsiveSize) {
    // Use component size configuration
    const { padding, fontSize, height } = responsiveSize;
    sizeClasses = `${padding ? `p-[${padding}]` : ''} ${fontSize ? `text-[${fontSize}]` : ''} ${height ? `h-[${height}]` : ''}`.trim();
  } else {
    // Use default responsive sizes
    sizeClasses = defaultSizes[size] || defaultSizes.md;
  }

  // Clean className to remove any space-x-* classes that might conflict
  const cleanClassName = className
    .split(' ')
    .filter(cls => !cls.startsWith('space-x-'))
    .join(' ');

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        ${baseClasses}
        ${variants[variant]}
        ${sizeClasses}
        ${fullWidth ? 'w-full' : ''}
        ${(disabled || loading) ? disabledClasses : ''}
        ${isMobile ? 'min-h-[44px]' : ''} 
        ${cleanClassName}
      `}
      style={{ gap: '0.5rem' }} // Force gap with inline style to override any conflicting classes
      {...props}
    >
      {loading && (
        <svg className={`animate-spin flex-shrink-0 ${iconSize}`} fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {children}
    </button>
  );
};

export default Button;